import React, { useEffect, useState } from 'react';
import RoyaltyStatus from '../components/royalty/RoyaltyStatus';
import RoyaltyMetrics from '../components/royalty/RoyaltyMetrics';
import RoyaltyRequirements from '../components/royalty/RoyaltyRequirements';
import RoyaltyDistribution from '../components/royalty/RoyaltyDistribution';
import { useWeb3 } from '../contexts/Web3Context';
import { useUI } from '../contexts/UIContext';
import { ethers } from 'ethers';

const Royalty = () => {
  const { userId, userInfo, grichInvContract, claimRoyaltyIncome, isConnected } = useWeb3();
  const { showLoading, hideLoading, showModal } = useUI();
  
  const [royaltyDetails, setRoyaltyDetails] = useState({
    isEligible: false,
    minInvestmentMet: false,
    minReferralsMet: false,
    qualifiedRefsMet: false,
    poolSize: '0',
    nextDistribution: 0
  });

  // Load royalty data
  useEffect(() => {
    if (userId > 0 && grichInvContract) {
      loadRoyaltyData();
    }
  }, [userId, grichInvContract]);

  const loadRoyaltyData = async () => {
    try {
      showLoading('Loading royalty data...');
      
      // Check royalty eligibility
      const eligibilityDetails = await grichInvContract.getRoyaltyQualificationDetails(userId);
      
      // Get royalty pool size
      const royaltyPool = await grichInvContract.royaltyPool();
      
      // Get distribution timer
      const lastDist = await grichInvContract.dividendLastDist();
      const nextDist = lastDist.toNumber() + 24 * 60 * 60; // 24 hours in seconds
      
      setRoyaltyDetails({
        isEligible: eligibilityDetails.isEligible,
        minInvestmentMet: eligibilityDetails.minInvestmentMet,
        minReferralsMet: eligibilityDetails.minReferralsMet,
        qualifiedRefsMet: eligibilityDetails.qualifiedRefsMet,
        poolSize: royaltyPool.toString(),
        nextDistribution: nextDist
      });
      
      hideLoading();
    } catch (error) {
      console.error('Error loading royalty data:', error);
      hideLoading();
      showModal('Error', 'Failed to load royalty data. Please try again.');
    }
  };

  // Handle royalty claim
  const handleClaimRoyalty = async () => {
    try {
      const success = await claimRoyaltyIncome();
      if (success) {
        loadRoyaltyData(); // Refresh data after claiming
      }
    } catch (error) {
      console.error('Error claiming royalty:', error);
    }
  };

  // If not connected or no user ID, show message
  if (!isConnected || !userId || userId === 0) {
    return (
      <>
        <h1>Royalty <span className="highlight">Pool</span> Rewards</h1>
        <p className="section-description">Earn passive income from the global royalty pool</p>
        
        <div className="not-connected-message" style={{ 
          textAlign: 'center', 
          background: 'var(--bg-card)', 
          padding: '50px 20px',
          borderRadius: '15px',
          margin: '40px 0'
        }}>
          <h2>{isConnected ? 'Make Your First Investment' : 'Connect Your Wallet'}</h2>
          <p style={{ marginTop: '15px', color: 'var(--text-muted)' }}>
            {isConnected 
              ? 'You need to make your first investment to access the royalty pool.'
              : 'Connect your wallet to view the royalty pool rewards.'}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <h1>Royalty <span className="highlight">Pool</span> Rewards</h1>
      <p className="section-description">Earn passive income from the global royalty pool</p>
      
      {/* Royalty Status */}
      <RoyaltyStatus 
        isEligible={royaltyDetails.isEligible}
        requirements={{
          minInvestmentMet: royaltyDetails.minInvestmentMet,
          minReferralsMet: royaltyDetails.minReferralsMet,
          qualifiedRefsMet: royaltyDetails.qualifiedRefsMet
        }}
      />
      
      {/* Royalty Metrics */}
      <RoyaltyMetrics 
        totalEarned={userInfo ? userInfo.royaltyIncomeUSDT : '0'}
        available={userInfo ? userInfo.currentRoyaltyIncomeUSDT : '0'}
        poolSize={royaltyDetails.poolSize}
        onClaim={handleClaimRoyalty}
        canClaim={
          royaltyDetails.isEligible && 
          userInfo && 
          parseFloat(userInfo.currentRoyaltyIncomeUSDT) > 0
        }
      />
      
      {/* Royalty Requirements */}
      <RoyaltyRequirements 
        requirements={{
          minInvestmentMet: royaltyDetails.minInvestmentMet,
          minReferralsMet: royaltyDetails.minReferralsMet,
          qualifiedRefsMet: royaltyDetails.qualifiedRefsMet
        }}
      />
      
      {/* Distribution Schedule */}
      <RoyaltyDistribution 
        nextDistribution={royaltyDetails.nextDistribution}
      />
    </>
  );
};

export default Royalty;
